import { useCallback, useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';

import { Alert } from '@zooplus/ui-components';

import BancontactAdyenCreditCardForm, {
  BancontactAdyenCreditCardFormRef,
} from '@components/BancontactAdyenCreditCardForm/BancontactAdyenCreditCardForm';
import DeleteCardModal from '@components/Overlays/Modals/DeleteCardModal/DeleteCardModal';
import StoredCreditCards from '@components/StoredCreditCards/StoredCreditCards';
import useEmergencyState from '@hooks/useEmergencyState';
import { useTranslation } from '@hooks/useTranslations';
import { useConfig } from '@providers/ConfigProvider';
import CoreProviders, { CoreProvidersProps } from '@providers/CoreProviders';
import CreditCardsMutationProvider, {
  useCreditcardService,
} from '@providers/CreditCardsMutationProvider';
import { useErrorContext } from '@providers/ErrorContextProvider';
import { captureException } from '@services/sentryService';
import { AdyenEnvironment } from '@types-internal/AdyenTypes';
import { CreditCardData, CustomerType } from '@types-internal/index';
import { exposeGlobally } from '@utils/utils';

import styles from './BancontactAdyenManageCreditCards.module.scss';

export interface BancontactAdyenManageCreditCardsProps extends CoreProvidersProps {
  containerType: 'myaccount' | 'default';
  customerType: CustomerType;
  currency: string;
  isFallbackPsp: boolean;
  adyenEnvironment: AdyenEnvironment;
  creditCards: CreditCardData[];
}

/*
 * On "My Account", we display the credit cards (if any, check done in StoredCreditCards).
 * And we provide ways to delete them, either individually or all at once.
 */
const BancontactAdyenManageCreditCards_MyAccount: React.FC<{ isFallbackPsp: boolean }> = ({
  isFallbackPsp,
}) => {
  return (
    <>
      <StoredCreditCards isMyAccount={true} isFallbackPsp={isFallbackPsp} isBancontact />
      <DeleteCardModal />
    </>
  );
};

/*
 * During checkout, when the customer has no credit cards, we display directly the form to add a new credit card.
 * This is the only action that the customer can do and that's why it does not make sense to use the NewBancontactAdyenCreditCardModal.
 *
 * We need to expose globally the 'saveBancontactAdyenCreditCard' since the submit is outside the form.
 */
const BancontactAdyenManageCreditCards_CheckoutNoCard: React.FC<
  BancontactAdyenManageCreditCardsProps
> = ({ adyenEnvironment, customerType, isFallbackPsp }) => {
  const { t } = useTranslation();
  const { config } = useConfig();
  const form = useRef<BancontactAdyenCreditCardFormRef>(null);
  const { setError } = useErrorContext();
  const creditcardService = useCreditcardService();
  const [showForm, setShowForm] = useState(true);

  const saveBancontactAdyenCreditCard = useCallback(
    async function (): Promise<boolean> {
      if (form.current === null) {
        setError({ message: t('checkout.payment.creditcard.error.addcard.apicall') });
        return false;
      } else {
        const bancontactAdyenFormData = form.current.computeBancontactAdyenCreditCardFormData();
        if (bancontactAdyenFormData === null) {
          return false;
        } else {
          return creditcardService
            .saveBancontactAdyenCreditCard(bancontactAdyenFormData)
            .then((isSuccess) => {
              if (isSuccess) {
                if (config.env !== 'local') {
                  window.location.assign('/checkout/preview.htm');
                }
              } else {
                setError({ message: t('checkout.payment.creditcard.error.addcard.apicall') });
              }

              return isSuccess;
            })
            .catch((error: unknown) => {
              // on any unexpected error, send it to Sentry and display a generic error message to the user
              captureException({ error });
              setError({ message: t('checkout.payment.creditcard.error.addcard.apicall') });
              return false;
            });
        }
      }
    },
    [form],
  );
  useEffect(
    () => exposeGlobally('saveBancontactAdyenCreditCard', saveBancontactAdyenCreditCard),
    [saveBancontactAdyenCreditCard],
  );

  const onBancontactFatalError = useCallback(
    (error: unknown, showForm?: boolean) => {
      captureException({ error });
      setError({
        message: t('checkout.payment.creditcard.error.addcard.apicall'),
      });
      setShowForm(typeof showForm === 'undefined' ? true : showForm);
    },
    [setError],
  );

  return (
    <>
      <Alert className={styles.adyenFormAlert}>
        <Trans
          t={t}
          i18nKey="checkout.payment.creditcard.3d.secure.message"
          components={{ url: <a target="_blank" href="/info/about/psd2_summary" /> }}
        />
      </Alert>
      {showForm && (
        <BancontactAdyenCreditCardForm
          ref={form}
          adyenEnvironment={adyenEnvironment}
          clientKey={config.adyen.clientKey}
          onFatalError={onBancontactFatalError}
          allowVaulting={!isFallbackPsp && customerType !== 'GUEST_CUSTOMER'}
        />
      )}
    </>
  );
};

/*
 * During checkout, when the customer has some credit cards, we display them and we provide ways to add a new one in a modal, and to delete the existing credit cards, either individually or all at once.
 */
const BancontactAdyenManageCreditCards_CheckoutWithCards: React.FC<
  BancontactAdyenManageCreditCardsProps
> = ({ isFallbackPsp }) => {
  return (
    <>
      <StoredCreditCards isMyAccount={false} isFallbackPsp={isFallbackPsp} isBancontact />
      <DeleteCardModal />
    </>
  );
};

/**
 * Dispatch to the proper UI to use depending on whether the customer has some credit cards or not.
 */
const BancontactAdyenManageCreditCards_Checkout: React.FC<BancontactAdyenManageCreditCardsProps> = (
  props,
) => {
  const {
    data: { creditCards },
  } = useCreditcardService();

  return (
    <>
      {creditCards.length > 0 ? (
        <BancontactAdyenManageCreditCards_CheckoutWithCards {...props} />
      ) : (
        <BancontactAdyenManageCreditCards_CheckoutNoCard {...props} />
      )}
    </>
  );
};

const BancontactAdyenManageCreditCardsSwitch: React.FC<BancontactAdyenManageCreditCardsProps> = (
  props,
) => {
  const { t } = useTranslation();
  const { setError } = useErrorContext();

  const { containerType, isFallbackPsp } = props;
  const isMyAccount = containerType === 'myaccount';

  useEmergencyState(isFallbackPsp, isMyAccount, t, setError);

  return isMyAccount ? (
    <BancontactAdyenManageCreditCards_MyAccount isFallbackPsp={isFallbackPsp} />
  ) : (
    <BancontactAdyenManageCreditCards_Checkout {...props} />
  );
};

/**
 * Main component which is covering all the possible UI on the 'editpayment' pages (My Account & Checkout funnel).
 */
const BancontactAdyenManageCreditCards: React.FC<BancontactAdyenManageCreditCardsProps> = (
  props,
) => {
  const { env, shopBundle, locale, domain, errorContainerId, csrfToken, creditCards } = props;

  return (
    <CoreProviders
      env={env}
      shopBundle={shopBundle}
      locale={locale}
      domain={domain}
      errorContainerId={errorContainerId}
      csrfToken={csrfToken}
      featureName="bancontact-adyen-credit-card">
      <CreditCardsMutationProvider
        pspToken={''}
        creditCards={creditCards}
        psp="ADYEN"
        paymentMethod="BANCONTACT">
        <BancontactAdyenManageCreditCardsSwitch {...props} />
      </CreditCardsMutationProvider>
    </CoreProviders>
  );
};

export default BancontactAdyenManageCreditCards;
